import { Button, Form, Heading } from "@ariakit/react";
import {
  DownloadLink,
  ExternalLink,
  MiddotSeparated,
  MinWidth,
  SubmitErrorAlert,
  useMediaQuery,
} from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import { Link } from "wouter";
import { fmt } from "../App/fmt.ts";
import { href } from "../App/href.tsx";
import { useCartContext } from "../cart/cart.tsx";
import {
  type FileDemo,
  type Product,
  type ProductOwnership,
} from "../client.ts";
import { fragment } from "../common.css.ts";
import { CheckoutButton } from "./CheckoutButton.tsx";
import { getKeyFacts, toType } from "./helpers.ts";
import { KeyFactsList } from "./KeyFacts.tsx";
import { ProductContext, useProduct } from "./ProductContext.tsx";
import { ProductLabel } from "./ProductType.tsx";
import { card, preview } from "./ShopPage.css.ts";
import { useCheckoutForm } from "./useCheckoutForm.tsx";

function ProductThumbnail() {
  const product = useProduct();

  if (!product.thumbnailSrc) {
    return null;
  }

  return <img src={product.thumbnailSrc} alt="" className={card.thumbnail} />;
}

function ProductHeader() {
  const product = useProduct();

  return (
    <header className={card.header}>
      <Heading className={card.heading}>{product.name}</Heading>
      <MiddotSeparated className={card.summary}>
        <span className={card.type}>{ProductLabel.get(product.type)}</span>
        {product.isEarlyAccess && <span className={card.ea}>Early Access</span>}
      </MiddotSeparated>
    </header>
  );
}

function ProductDescription() {
  const product = useProduct();

  if (!product.description) {
    return null;
  }

  return (
    <div className={card.description}>
      {product.description.split(/\n+/).map((paragraph, index) => {
        return (
          <p key={index} className={card.paragraph}>
            {paragraph}
          </p>
        );
      })}
    </div>
  );
}

import finsterfey from "./finsterfey.png";

function CollabInfo() {
  const product = useProduct();

  if (!product.description || !/finsterfey/.test(product.description)) {
    return null;
  }

  return (
    <div className={card.collab}>
      <p className={card.collabInfo}>Official collaboration with:</p>

      <ExternalLink href="http://www.finsterfey.com/" className={fragment}>
        <img
          src={finsterfey}
          alt="Finsterfey Miniatures"
          className={card.collabLogo}
        />
      </ExternalLink>
    </div>
  );
}

function ProductKeyFacts() {
  const product = useProduct();

  return <KeyFactsList facts={getKeyFacts(product)} productsCount={1} />;
}

function ProductPreview() {
  const product = useProduct();

  if (!product.previewSrcs.length) {
    return null;
  }

  return (
    <div className={preview.container}>
      <div className={preview.track}>
        {product.previewSrcs.map((src, index) => {
          return (
            <img
              key={src}
              src={src}
              className={preview.image}
              alt=""
              loading={index === 0 ? "eager" : "lazy"}
            />
          );
        })}

        <div className={preview.spacer} />
      </div>

      <div className={preview.hint}>← Scroll to explore →</div>
    </div>
  );
}

function OwnedProductActions(props: {
  ownership: ProductOwnership;
  demo?: FileDemo;
}) {
  const { ownership, demo } = props;

  return (
    <div className={card.actions}>
      {ownership.downloadUrl ? (
        <DownloadLink className={card.buy} href={ownership.downloadUrl}>
          Download ({ownership.downloadFileSize})
        </DownloadLink>
      ) : (
        <div className={card.note}>
          Failed to generate download link. Please get in touch with
          support@indietabletop.club.
        </div>
      )}

      {demo && (
        <DownloadLink className={card.try} href={demo.src}>
          Quick Start Rules
        </DownloadLink>
      )}

      <div className={card.note}>
        Added to library
        <br />
        {fmt.date(ownership.grantedTs)}
      </div>
    </div>
  );
}

function UnownedProductActions(props: { demo: FileDemo | undefined }) {
  const { demo } = props;

  const product = useProduct();
  const cart = useCartContext();
  const inCart = cart.has(product.id);
  const { form, submitName } = useCheckoutForm({
    products: [product],
    cancelTo: href("~/shop", { fragment: product.code }),
  });

  return (
    <div className={card.actions}>
      <Form store={form} className={fragment}>
        <CheckoutButton
          products={[product]}
          className={card.buy}
          backToIfAccountNeeded={href("~/shop", {
            fragment: product.code,
          })}
          redirectToIfAccountNeeded={href("~/shop", {
            search: { note: "continuePurchase" },
            fragment: product.code,
          })}
        >
          Buy {fmt.currency(product.currency, product.price)}
        </CheckoutButton>

        <SubmitErrorAlert name={submitName} />
      </Form>

      {inCart ? (
        <Button
          className={card.cart}
          onClick={() => void cart.remove(product.id)}
        >
          <svg
            width="17"
            height="13"
            viewBox="0 0 17 13"
            style={{
              display: "inline",
              verticalAlign: "bottom",
              marginInline: "-0.5ch 0.5ch",
              translate: "0 -0.1em",
            }}
            fill="#2DB341"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M6.36396 9.19238L15.5564 0L16.9706 1.41421L6.36396 12.0208L0 5.65688L1.41422 4.24268L6.36396 9.19238Z" />
          </svg>
          Added to cart
        </Button>
      ) : (
        <Button className={card.cart} onClick={() => void cart.add(product.id)}>
          Add to cart
        </Button>
      )}

      {demo && (
        <DownloadLink className={card.try} href={demo.src}>
          Try for free
        </DownloadLink>
      )}

      {product.type === "DIGITAL_BOOK" && (
        <div className={card.note}>
          <Link
            className={interactiveText}
            href={`~/docs/help/accessing-owned-content?${new URLSearchParams({ backTo: href("~/shop") })}`}
          >
            I already own this product
          </Link>
        </div>
      )}
    </div>
  );
}

function ProductActions() {
  const product = useProduct();
  const demo = product.demos.find((demo) => demo.type === "FILE");

  if (product.ownership) {
    return <OwnedProductActions ownership={product.ownership} demo={demo} />;
  }

  return <UnownedProductActions demo={demo} />;
}

export function ProductCard(props: { product: Product }) {
  const { product } = props;
  const domId = product.code;
  const isMediumOrWider = useMediaQuery(MinWidth.MEDIUM);

  if (isMediumOrWider) {
    return (
      <ProductContext value={product}>
        <div id={domId} className={card.container({ type: toType(product) })}>
          <div className={card.sidebar}>
            <ProductThumbnail />
            <ProductActions />
            <CollabInfo />
          </div>

          <div className={card.divider} />

          <div className={card.content}>
            <ProductHeader />

            <ProductDescription />

            <ProductKeyFacts />

            <ProductPreview />
          </div>
        </div>
      </ProductContext>
    );
  }

  return (
    <ProductContext value={product}>
      <div id={domId} className={card.container({ type: toType(product) })}>
        <ProductThumbnail />

        <div className={card.content}>
          <ProductHeader />
          <ProductActions />

          <ProductDescription />

          <ProductKeyFacts />

          <CollabInfo />
        </div>

        <ProductPreview />
      </div>
    </ProductContext>
  );
}
